import { fetchApi, trackError } from '@vend/utilities'
import { useState } from 'react'
import { LineItem } from '../../../models/line-item'

export const BREAKDOWN_API_URL = '/api/2.0/products/actions/breakdown'

export const getRequiredOuterPacks = (
  innerPacksPerOuterPack: number,
  innerPackInventoryLevel: number,
  requestedQuantity: number
) => {
  if (innerPacksPerOuterPack <= 0) {
    throw new Error('Invalid inner packs per outer pack')
  }

  const shortfall = requestedQuantity - innerPackInventoryLevel

  if (shortfall <= 0) {
    return 0 // No breakdown needed
  }

  return Math.ceil(shortfall / innerPacksPerOuterPack)
}

export const useProductPackagingBreakdown = (
  lineItem: LineItem,
  outletId: string,
  innerPackInventoryLevel: number,
  enabled: boolean = true
) => {
  const [loading, setLoading] = useState(false)

  if (!enabled) {
    return { breakdownOuterPacks: async () => false, loading: false }
  }

  const outerPackPackaging = lineItem.product?.packaging?.made_from?.[0]
  const outerPackId = outerPackPackaging?.package_id
  const innerPacksPerOuterPack = outerPackPackaging?.amount || 0
  const requestedQuantity = Number(lineItem.quantity)

  const breakdownOuterPacks = async () => {
    setLoading(true)

    try {
      if (!outerPackId) {
        throw new Error('Invalid product packaging: missing outer pack ID')
      }

      if (innerPacksPerOuterPack <= 0) {
        throw new Error(
          'Invalid product packaging: conversion ratio must be greater than 0'
        )
      }

      if (innerPackInventoryLevel >= requestedQuantity) {
        throw new Error('Breakdown not needed: sufficient inventory available')
      }

      const requiredOuterPacks = getRequiredOuterPacks(
        innerPacksPerOuterPack,
        innerPackInventoryLevel,
        requestedQuantity
      )

      const response = await fetchApi(BREAKDOWN_API_URL, {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          outlet_id: outletId,
          packaging: [
            {
              package_id: outerPackId,
              quantity: requiredOuterPacks,
            },
          ],
        }),
        method: 'POST',
      })

      if (!response.ok) {
        throw new Error('Breakdown API request failed', { cause: response })
      }

      return true
    } catch (error) {
      trackError('Failed to breakdown outer packs', {
        error,
        lineItem,
        innerPackInventoryLevel,
        outletId,
      })

      return false
    } finally {
      setLoading(false)
    }
  }

  return { breakdownOuterPacks, loading }
}
